import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Download, FileDown, ListChecks, Search } from 'lucide-react';
import { dataSize } from './dataSize';
import { downloadSharedList, loadSharedData, loadSharedFiles, openSharedFile } from './customerApi';
import { tx } from './i18n';
import { errorMessage } from './ui';
import './customerSharedData.css';

// What our laboratory shared on a project, as the customer finds it on the project's page: two views,
// the files of the whole project and the samples that have files. However many samples a project
// has it stays the same: a sample is found by its name, a page at a time, and opens where it is, with
// the laboratory's note above its files. One button a file, and for hundreds of files a list of links
// for a download tool. It appears once the laboratory has shared something.
const t = (key, values) => tx(`ui.customer_portal.${key}`, values);
const day = (value) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
const shortDay = (value) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(value));

function Batches({ data, onError }) {
  return data.batches.map((batch) => <div key={batch.uuid} className="xo-cpdata-batch">
    <small>{batch.sharedBy ? t('shared_by', { date: day(batch.sharedAt), name: batch.sharedBy }) : day(batch.sharedAt)}</small>
    {batch.note && <p className="xo-cpdata-note">{batch.note}</p>}
    <ul>{batch.files.map((file) => <li key={file.uuid}>
      <span>{file.name}</span><small>{dataSize(file.bytes)}</small>
      <button type="button" className="xo-btn-2" aria-label={t('shared_download_named', { name: file.name })}
        onClick={() => openSharedFile(file.uuid).catch((caught) => onError(errorMessage(caught)))}><Download aria-hidden="true" />{t('shared_download')}</button>
    </li>)}</ul>
  </div>);
}

// The files of the whole project or of one sample, read when they are opened.
function Files({ project, sample, onError }) {
  const [data, setData] = useState(null);
  const load = useCallback((offset) => loadSharedFiles(project.uuid, sample, offset)
    .then((next) => setData((old) => (offset === 0 || old === null ? next : { ...next, batches: [...old.batches, ...next.batches] })))
    .catch((caught) => onError(errorMessage(caught))), [project.uuid, sample, onError]);
  useEffect(() => { load(0); }, [load]);
  if (data === null) return <p className="xo-cust-quiet">{t('loading')}</p>;
  if (data.total === 0) return <p className="xo-cust-quiet">{t('shared_none_project')}</p>;
  const shown = data.batches.reduce((sum, batch) => sum + batch.files.length, 0);
  return <>
    <Batches data={data} onError={onError} />
    {shown < data.total && <button type="button" className="xo-btn-2" onClick={() => load(shown)}>{t('shared_more')}</button>}
  </>;
}

export default function CustomerSharedData({ project }) {
  const [view, setView] = useState(null), [query, setQuery] = useState(''), [offset, setOffset] = useState(0);
  const [data, setData] = useState(null), [open, setOpen] = useState(null), [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    const timer = window.setTimeout(() => {
      loadSharedData(project.uuid, { q: query.trim(), offset }).then((next) => { if (current) setData(next); })
        .catch((caught) => { if (current) setError(errorMessage(caught)); });
    }, query === '' ? 0 : 250);
    return () => { current = false; window.clearTimeout(timer); };
  }, [project.uuid, query, offset]);
  if (data === null || data.totals.files === 0) return null;
  const { totals, samples } = data;
  const shown = view ?? (totals.samplesWithFiles > 0 ? 'samples' : 'project');
  const list = (sample) => downloadSharedList(project.uuid, sample).catch((caught) => setError(errorMessage(caught)));
  const last = Math.min(samples.offset + samples.limit, samples.total);
  return <section id="shared-data" className="xo-cp-card xo-cpdata">
    <div className="xo-cpdata-head">
      <div><h3>{t('shared_title')}</h3><p className="xo-cp-card-note">{t('shared_detail')}</p></div>
      <button type="button" className="xo-btn-2" onClick={() => list(null)}><ListChecks aria-hidden="true" />{t('shared_list')}</button>
    </div>
    <div className="xo-cpdata-bar">
      <div className="xo-seg" role="tablist">
        <button type="button" role="tab" aria-selected={shown === 'project'} className={shown === 'project' ? 'is-on' : ''} onClick={() => setView('project')}>
          {t('shared_whole_project')} <em>{totals.projectFiles}</em></button>
        <button type="button" role="tab" aria-selected={shown === 'samples'} className={shown === 'samples' ? 'is-on' : ''} onClick={() => setView('samples')}>
          {t('shared_by_sample')} <em>{t('shared_samples_of', { shared: totals.samplesWithFiles, total: totals.samples })}</em></button>
      </div>
      {shown === 'samples' && <label className="xo-cpdata-find"><Search aria-hidden="true" />
        <input type="search" value={query} placeholder={t('shared_find')} aria-label={t('shared_find')}
          onChange={(event) => { setQuery(event.target.value); setOffset(0); setOpen(null); }} /></label>}
    </div>
    <p className="xo-cp-card-note">{t('shared_list_hint')}</p>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {shown === 'project' ? <Files project={project} sample={null} onError={setError} /> : <>
      {samples.rows.length === 0 ? <p className="xo-cust-quiet">{t('shared_none_found')}</p> : <div className="xo-cpdata-table">
        <div className="xo-cpdata-row is-head"><span>{t('shared_sample')}</span><span>{t('shared_files')}</span><span>{t('shared_size')}</span><span>{t('shared_when')}</span><span /></div>
        {samples.rows.map((row) => {
          const opened = open === row.position;
          return <div key={row.position} className={`xo-cpdata-sample${opened ? ' is-open' : ''}`}>
            <button type="button" className="xo-cpdata-row" aria-expanded={opened} aria-label={t('shared_open', { name: row.sampleName })}
              onClick={() => setOpen(opened ? null : row.position)}>
              <strong>{row.sampleName}</strong><span>{row.files}</span><span>{dataSize(row.bytes)}</span><span>{shortDay(row.sharedAt)}</span>
              {opened ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
            </button>
            {opened && <div className="xo-cpdata-open">
              <Files project={project} sample={row.position} onError={setError} />
              {row.files > 1 && <button type="button" className="xo-cpdata-list" onClick={() => list(row.position)}><FileDown aria-hidden="true" />{t('shared_list')}</button>}
            </div>}
          </div>;
        })}
      </div>}
      {samples.total > samples.limit && <div className="xo-cpdata-pager">
        <span>{t('shared_page', { from: samples.offset + 1, to: last, total: samples.total })}</span>
        <span>
          <button type="button" className="xo-icon-btn" disabled={samples.offset === 0} aria-label={t('shared_previous')}
            onClick={() => { setOffset(Math.max(0, samples.offset - samples.limit)); setOpen(null); }}><ChevronLeft aria-hidden="true" /></button>
          <button type="button" className="xo-icon-btn" disabled={last >= samples.total} aria-label={t('shared_next')}
            onClick={() => { setOffset(samples.offset + samples.limit); setOpen(null); }}><ChevronRight aria-hidden="true" /></button>
        </span>
      </div>}
    </>}
  </section>;
}
